import { FC } from 'react';
import { isNull } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { ensureHttpPrefix } from '@tock/shared/utils/urlUtils';
import { isLocationDisplayable } from 'utils/locationUtils';
import { shortenUrlToHost } from 'utils/pathUtils';
import Anchor from 'components/Anchor';
import BusinessMapWithScript from 'components/business/landing-page/BusinessMapWithScript';
import FaqLink from 'components/business/landing-page/FaqLink';
import SocialLinks from 'components/business/landing-page/SocialLinks';
import googleMaps from '@tock/shared/utils/googleMaps';

interface Props {
  business: consumer.IBusinessConfig;
  ticketTypeDetails: core.TicketTypeDetails | null;
  deliveryLocation?: consumer.ILocation;
}

const ReceiptLocationPanelRow: FC = ({ children }) => (
  <div className="ReceiptLocationPanel-row Receipt-container--section">{children}</div>
);

const ReceiptLocationPanel: FC<Props> = ({ business, deliveryLocation, ticketTypeDetails }) => {
  let location = oc(ticketTypeDetails).location(null) as core.ILocation;
  const isAtBusinessLocation = oc(ticketTypeDetails).isAtBusinessLocation(false);
  if (isAtBusinessLocation) {
    const businessLocation: core.ILocation = {
      name: oc(business).name(''),
      address: oc(business).address(''),
      address2: oc(business).address2(''),
      city: oc(business).city(''),
      state: oc(business).state(''),
      country: oc(business).country(''),
      zipCode: oc(business).zipCode(''),
      googlePlacesId: '',
    };
    location = businessLocation;
  }
  if (deliveryLocation) {
    location = deliveryLocation;
  }
  const isDelivery = !!deliveryLocation;

  const addressString = googleMaps.getAddressString(
    oc(location).address(''),
    oc(location).city(''),
    oc(location).state(''),
    oc(location).country(''),
    oc(location).zipCode('')
  );

  const shouldDisplayBusinessAddress = business.shouldDisplayAddress;
  const shouldDisplayAddress =
    isLocationDisplayable(location) &&
    (!(isAtBusinessLocation && !shouldDisplayBusinessAddress) || isDelivery);

  return (
    <div className="ReceiptLocationPanel">
      {!isNull(location) && shouldDisplayAddress && (
        <BusinessMapWithScript
          addressFields={location}
          businessName={oc(business).name('')}
          businessDomainName={oc(business).domainName('')}
          isAtBusinessLocation={isAtBusinessLocation}
          lazy
          modifierClassName="ReceiptMap-mapContainer"
          analyticsEventCategory="Receipt Map"
          googlePlacesId={
            isAtBusinessLocation && !isDelivery ? business.googlePlacesId : location.googlePlacesId
          }
        />
      )}
      <div className="ReceiptLocationPanel-content">
        <Typography color="inherit" component="span" variant="body02">
          {shouldDisplayAddress && (
            <ReceiptLocationPanelRow>
              <Anchor
                href={`https://www.google.com/maps/dir//${addressString}`}
                target="_blank"
                className="u-link"
                data-testid="receipt-business-link"
              >
                <Typography color="inherit" component="span" variant="body02Strong">
                  {isDelivery ? 'Delivery address' : location.name}
                  {!isAtBusinessLocation && !isDelivery && (
                    <span className="u-lightColor">
                      <Typography variant="caption" component="span">
                        {' '}
                        - Different from {business.name}
                      </Typography>
                    </span>
                  )}
                  <br />
                </Typography>
                <span data-testid="street-address">
                  {location.address}
                  <br />
                </span>
                {location.address2 && (
                  <span data-testid="apt-unit">
                    {location.address2}
                    <br />
                  </span>
                )}
                <span data-testid="city-state-zip">
                  {location.city}, {location.state} {location.zipCode}
                </span>
              </Anchor>
            </ReceiptLocationPanelRow>
          )}
          {business.customerFacingPhone && (
            <ReceiptLocationPanelRow>
              <Anchor href={`tel:${business.customerFacingPhone}`} className="u-link">
                {business.customerFacingPhone}
              </Anchor>
            </ReceiptLocationPanelRow>
          )}
          {business.webUrl && (
            <ReceiptLocationPanelRow>
              <Anchor
                target="_blank"
                href={ensureHttpPrefix(business.webUrl)}
                trackingCategory="sidebarLinks"
                className="u-link u-activeColor"
              >
                {shortenUrlToHost(business.webUrl)}
              </Anchor>
            </ReceiptLocationPanelRow>
          )}
          <FaqLink wrapper={ReceiptLocationPanelRow}>Frequently asked questions</FaqLink>
          <SocialLinks
            facebookUrl={oc(business).facebookUrl('')}
            instagramId={oc(business).instagramId('')}
            twitterHandle={oc(business).twitterHandle('')}
          />
        </Typography>
      </div>
    </div>
  );
};

export default ReceiptLocationPanel;
